import { useId, useState, type ComponentProps, type ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Button from "@/v2-backoffice-client/components/button"

export type PageTitleProps = ComponentProps<"h1"> & {
  /**
   * What the page, or its active tab, is for: a paragraph for the operator
   * who asks, not a banner for everyone. Behind a labelled disclosure next
   * to the title ("What is this?"), shown under the header row when opened,
   * so the header stays one line with the actions on it. Pass the active
   * tab's text and it swaps in place while open.
   */
  about?: ReactNode
}

function PageTitle(props: PageTitleProps) {
  const { className, about, ...h1Props } = props

  const aboutId = useId()
  const [isAboutOpen, setIsAboutOpen] = useState(false)

  const title = <h1 className={cn("page-title", className)} {...h1Props} />
  if (!about) return title

  return (
    <>
      <div className="page-title-row">
        {title}
        <Button
          type="button"
          variant="ghost"
          size="tiny"
          iconSrc="info"
          iconSize={12}
          aria-expanded={isAboutOpen}
          aria-controls={aboutId}
          className={cn(
            "page-about-toggle",
            isAboutOpen && "page-about-toggle--open",
          )}
          onClick={() => setIsAboutOpen((open) => !open)}
        >
          What is this?
          <Icon
            aria-hidden
            src="angle-small-down"
            size={12}
            className="page-about-toggle-chevron"
          />
        </Button>
      </div>
      {/* A full-width flex child of the header, ordered after the actions
          (page.css), so the text gets its own line under title and actions
          and never squeezes them. */}
      {isAboutOpen && (
        <div id={aboutId} className="page-about">
          <p className="page-about-text">{about}</p>
        </div>
      )}
    </>
  )
}

export default PageTitle
